import { Box, Group, Text, UnstyledButton } from '@mantine/core';
import { signOut } from 'firebase/auth';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminGlyph, BottomTabBar, ProfileGlyph, PwaInstallBanner, RouteGlyph } from '@/components';
import { useAuth } from '@/hooks';
import { auth } from '@/services';

interface AppLayoutProps {
  children: ReactNode;
}

const NAV_TABS = (isAdmin: boolean, t: (key: string) => string) => [
  { to: '/', label: t('nav.today'), icon: <RouteGlyph /> },
  { to: '/profile', label: t('nav.profile'), icon: <ProfileGlyph /> },
  ...(isAdmin ? [{ to: '/admin', label: t('nav.admin'), icon: <AdminGlyph /> }] : []),
];

/**
 * App shell for signed-in pages: a slim top status strip (identity + sign
 * out) and a fixed bottom tab bar for primary navigation — the mobile app
 * navigation pattern, since this is a phone-first tool used in the field.
 */
export const AppLayout = ({ children }: AppLayoutProps) => {
  const { appUser, isAdmin } = useAuth();
  const { t } = useTranslation();

  return (
    <Box mih="100vh" pb={72} style={{ display: 'flex', flexDirection: 'column' }}>
      <Group
        component="header"
        h={44}
        justify="space-between"
        px="md"
        style={{
          borderBottom: '1px solid var(--mantine-color-ink-3)',
          flexShrink: 0,
          paddingTop: 'var(--safe-top)',
        }}
      >
        <Text c="ink.7" ff="monospace" fw={600} size="xs" style={{ letterSpacing: '0.08em' }}>
          {appUser ? `OP/${appUser.displayName.split(' ')[0]?.toUpperCase()}` : t('nav.brand')}
        </Text>
        <Group gap="md" wrap="nowrap">
          <UnstyledButton
            c="ink.7"
            ff="monospace"
            fw={600}
            onClick={() => signOut(auth)}
            style={{ fontSize: '0.6875rem', letterSpacing: '0.08em' }}
          >
            {t('nav.signOut')}
          </UnstyledButton>
        </Group>
      </Group>

      <PwaInstallBanner />

      <Box style={{ flex: 1 }}>{children}</Box>

      <BottomTabBar tabs={NAV_TABS(isAdmin, t)} />
    </Box>
  );
};
